<script setup lang="ts">
import { useI18N } from '@dialpad/i18n';
import { DATA_QA } from './csv-name-input.constants';
import { DtInput } from '@dialpad/dialtone/vue3/lib/input';
import { computed, inject, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import logger from '@/logger';

const model = defineModel<string>({
  required: true,
});
type Props = {
  label?: string;
  placeholder?: string;
};
defineProps<Props>();

const log = logger.getChild('CsvNameInput');
const { $t } = useI18N();

const validationTrigger = inject<Ref<number>>(TRIGGER_FORM_VALIDATION, ref(0));

enum CsvNameErrorType {
  REQUIRED = 'REQUIRED',
  OVER_LIMIT = 'OVER_LIMIT',
}
const emit =
  defineEmits<(e: 'update:error', value: CsvNameErrorType | null) => void>();

const validationError = ref<CsvNameErrorType | null>(null);

watch(model, () => {
  validationError.value = null;
});

watch(validationError, () => {
  log.debug('Validation error update', validationError.value);
  emit('update:error', validationError.value);
});

const validate = () => {
  if (model.value.length === 0) {
    validationError.value = CsvNameErrorType.REQUIRED;
    return;
  }

  if (model.value.length > validateData.length.max) {
    validationError.value = CsvNameErrorType.OVER_LIMIT;
    return;
  }

  validationError.value = null;
};

watch(validationTrigger, () => {
  log.debug('Validation trigger');
  validate();
});

const validateData = {
  length: {
    description: $t('CSV_NAME_INPUT_DESCRIPTION'),
    max: 50,
    warn: 30,
    limitMaxLength: false,
  },
};

const validationMessage = () => {
  const remainingCharacters = validateData.length.max - model.value.length;

  if (remainingCharacters < 0) {
    return $t('CSV_NAME_INPUT_OVER_LIMIT', {
      count: Math.abs(remainingCharacters),
    });
  }

  return $t('CSV_NAME_INPUT_CHARACTERS_LEFT', {
    count: remainingCharacters,
  });
};

const validateInput = () => {
  return {
    length: {
      ...validateData.length,
      message: validationMessage(),
    },
  };
};

type InputMessage = {
  message: string;
  type: 'error' | 'success' | 'warning';
};
const validationMessages = computed(() => {
  const result: InputMessage[] = [];
  if (validationError.value === CsvNameErrorType.REQUIRED) {
    const message = $t('CSV_NAME_INPUT_ERROR_REQUIRED');
    result.push({
      message,
      type: 'error',
    });
  }

  return result;
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <DtInput
      v-model="model"
      :data-qa="DATA_QA.INPUT"
      :label="label ?? ''"
      :placeholder="placeholder ?? $t('CSV_NAME_INPUT_PLACEHOLDER')"
      :validate="validateInput()"
      :messages="validationMessages"
    />
  </div>
</template>
